/**
 * Unsaved Changes Dialog.
 *
 * Shown when a user tries to navigate away from a form with unsaved
 * changes. Offers three options: save as draft, discard, or stay.
 */

import { useState } from "react";
import { AlertTriangle, Save, Trash2, ArrowLeft } from "lucide-react";
import Button from "@/components/ui/Button";

interface UnsavedChangesDialogProps {
  isOpen: boolean;
  onSaveDraft?: () => Promise<void>;
  onDiscard: () => void;
  onStay: () => void;
  entityName?: string;
}

function UnsavedChangesDialog({
  isOpen,
  onSaveDraft,
  onDiscard,
  onStay,
  entityName = "record",
}: UnsavedChangesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!onSaveDraft) return;
    setIsSaving(true);
    try {
      await onSaveDraft();
      // Save succeeded — close the dialog so navigation can proceed.
      onDiscard();
    } catch {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onStay}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md rounded-[var(--radius-xl)] elevation-3 p-6 animate-scale-in">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--fill-warning)]">
            <AlertTriangle className="h-5 w-5 text-[var(--text-warning)]" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              Unsaved Changes
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              You have unsaved changes to this {entityName}. What would you like
              to do?
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" size="sm" onClick={onStay}>
            <ArrowLeft className="h-3.5 w-3.5" /> Stay
          </Button>
          <Button variant="secondary" size="sm" onClick={onDiscard}>
            <Trash2 className="h-3.5 w-3.5" /> Discard
          </Button>
          {onSaveDraft && (
            <Button size="sm" onClick={handleSave} isLoading={isSaving}>
              <Save className="h-3.5 w-3.5" /> Save Draft
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default UnsavedChangesDialog;
